import { getBackendUrl } from "@/lib/backend";
import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import DemoBookingDialog from "@/components/DemoBookingDialog";

const API = `${getBackendUrl()}/api`;

export default function Waitlist() {
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, form);
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", role: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="talk-to-us" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="relative glass-panel rounded-3xl p-8 md:p-14 overflow-hidden"
        >
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#D4AF37]/5 blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            <div className="lg:col-span-2">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-5">Talk to us</p>
              <h2 className="font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-white leading-[1.1]">
                Have a project in mind? <span className="italic gold-gradient-text">Let&apos;s chat.</span>
              </h2>
              <p className="mt-6 text-white/55 text-sm md:text-base leading-relaxed">
                Book a 15-min walkthrough with the Facetra team, or drop your details and we&apos;ll reach out
                with a personalised onboarding — Jadau, Polki, Meenakari, whatever you build.
              </p>
              <div className="mt-8 flex items-center gap-3 text-xs text-white/45">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                Or sign up instantly — no waitlist, 10 free credits on account creation
              </div>
            </div>

            <form onSubmit={submit} data-testid="waitlist-form" className="lg:col-span-3 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">Name</label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="waitlist-input-name"
                    className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/25 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                    placeholder="Aanya Mehra"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="waitlist-input-email"
                    className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/25 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                    placeholder="you@atelier.com"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">Company</label>
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    data-testid="waitlist-input-company"
                    className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/25 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                    placeholder="Mehra Atelier"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">Role</label>
                  <input
                    name="role"
                    value={form.role}
                    onChange={onChange}
                    data-testid="waitlist-input-role"
                    className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/25 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
                    placeholder="Creative Director"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2 block">What would you build?</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={3}
                  data-testid="waitlist-input-message"
                  className="w-full bg-[#141414] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/25 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none"
                  placeholder="Bridal sets, polki layering, manufacturing references…"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={loading || done}
                  data-testid="talk-to-us-submit"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-7 py-3.5 hover:bg-[#F3CE5A] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
                >
                  {done ? "Message sent ✓" : loading ? "Sending…" : "Send us a note →"}
                </button>
                <DemoBookingDialog
                  trigger={
                    <button
                      type="button"
                      data-testid="talk-to-us-book-demo"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.03] text-white text-sm px-6 py-3.5 hover:bg-white/[0.07] hover:border-[#D4AF37]/40 transition-all duration-300"
                    >
                      Or — book a 15-min walkthrough
                    </button>
                  }
                />
              </div>
              {done ? (
                <div className="mt-3 flex items-center gap-3 text-sm text-[#D4AF37]" data-testid="talk-to-us-success">
                  <span>We&apos;ll get back within 48 hours.</span>
                  <a
                    href="/signup"
                    className="underline underline-offset-4 hover:text-[#F3CE5A]"
                  >
                    Or start now — create your account →
                  </a>
                </div>
              ) : (
                <span className="block text-xs text-white/40 pt-1">No spam. We reach out within 48 hours.</span>
              )}
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
